import { useState, useEffect } from "react";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { SectionCard, Th, Td, Empty, Stars } from "../components/shared";
import { inr, inrShort, pct } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
  PieChart, Pie, Cell, LineChart, Line,
} from "recharts";
import { BrainCircuit, Sparkles, Send, Loader2, Ship, Store, ArrowRight, TrendingUp, Wallet, ShoppingCart, Percent, Save, Bookmark, Trash2 } from "lucide-react";

const COLORS = ["#0D9488", "#059669", "#0F172A", "#F59E0B", "#64748B", "#38BDF8", "#A78BFA"];

function renderMd(text) {
  return String(text).split("\n").map((line, i) => {
    if (line.startsWith("### ")) return <h4 key={i} className="font-heading font-bold text-navy mt-3 mb-1">{line.replace("### ", "")}</h4>;
    const bolded = line.replace(/\*\*(.+?)\*\*/g, "<b>$1</b>");
    if (line.startsWith("- ") || line.startsWith("* ")) return <li key={i} className="ml-4 list-disc text-sm text-slate-700" dangerouslySetInnerHTML={{ __html: bolded.replace(/^[-*]\s/, "") }} />;
    if (!line.trim()) return <div key={i} className="h-1" />;
    return <p key={i} className="text-sm text-slate-700" dangerouslySetInnerHTML={{ __html: bolded }} />;
  });
}

function AiTotals({ t }) {
  const cards = [
    { label: "Total Spend", value: inrShort(t.spend), icon: Wallet },
    { label: "Total Saved", value: inrShort(t.saved), icon: TrendingUp },
    { label: "Orders", value: t.orders, icon: ShoppingCart },
    { label: "Avg Order Value", value: inrShort(t.avg_order), icon: Percent },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((c) => (
        <div key={c.label} className="bg-white rounded-xl border border-border p-5 flex items-center justify-between">
          <div><div className="text-xs uppercase tracking-widest text-brand-slate font-semibold">{c.label}</div><div className="font-heading text-2xl font-bold text-navy mt-1">{c.value}</div></div>
          <c.icon className="w-6 h-6 text-brand-teal" />
        </div>
      ))}
    </div>
  );
}

function SourcingTool() {
  const { meta } = useApp();
  const [f, setF] = useState({ item_name: "", qty: 100, local_price: 0, import_price: 0, shipping_total: 0, duty_pct: 18, lead_time_local: 7, lead_time_import: 30, import_origin: "China" });
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [presets, setPresets] = useState([]);
  const [presetName, setPresetName] = useState("");
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const loadPresets = () => api.get("/sourcing/presets").then((r) => setPresets(r.data));
  useEffect(() => { loadPresets(); }, []);

  const savePreset = async () => {
    const name = presetName.trim() || f.item_name || "Untitled comparison";
    await api.post("/sourcing/presets", { name, ...f, qty: Number(f.qty), local_price: Number(f.local_price), import_price: Number(f.import_price), shipping_total: Number(f.shipping_total), duty_pct: Number(f.duty_pct), lead_time_local: Number(f.lead_time_local), lead_time_import: Number(f.lead_time_import) });
    toast.success("Preset saved for reuse"); setPresetName(""); loadPresets();
  };

  const applyPreset = (p) => {
    setF({ item_name: p.item_name, qty: p.qty, local_price: p.local_price, import_price: p.import_price, shipping_total: p.shipping_total, duty_pct: p.duty_pct, lead_time_local: p.lead_time_local, lead_time_import: p.lead_time_import, import_origin: p.import_origin });
    setRes(null); toast.success(`Loaded "${p.name}"`);
  };

  const deletePreset = async (e, id) => {
    e.stopPropagation();
    await api.delete(`/sourcing/presets/${id}`); toast.success("Preset removed"); loadPresets();
  };

  const run = async () => {
    if (!f.item_name) return;
    setLoading(true);
    try {
      const r = await api.post("/ai/sourcing", {
        ...f, qty: Number(f.qty), local_price: Number(f.local_price), import_price: Number(f.import_price),
        shipping_total: Number(f.shipping_total), duty_pct: Number(f.duty_pct),
        lead_time_local: Number(f.lead_time_local), lead_time_import: Number(f.lead_time_import),
      });
      setRes(r.data);
    } finally { setLoading(false); }
  };

  const c = res?.calc;
  const cheaperImport = c && c.cheaper === "import";

  return (
    <SectionCard title="Import vs Local Sourcing Comparison">
      <p className="text-sm text-brand-slate mb-4">Compare landed cost of importing (e.g. from China) against your existing local vendors, with an AI verdict on whether it's worth it.</p>

      {presets.length > 0 && (
        <div className="mb-4" data-testid="sourcing-presets">
          <div className="flex items-center gap-2 text-xs font-semibold text-brand-slate uppercase tracking-widest mb-2"><Bookmark className="w-3.5 h-3.5" /> Saved comparisons</div>
          <div className="flex flex-wrap gap-2">
            {presets.map((p) => (
              <button key={p.id} data-testid="preset-chip" onClick={() => applyPreset(p)}
                className="group flex items-center gap-2 text-sm bg-slate-100 hover:bg-teal-50 border border-slate-200 hover:border-brand-teal rounded-full pl-3 pr-2 py-1.5 transition-colors">
                <span className="text-navy font-medium">{p.name}</span>
                <button type="button" data-testid="preset-delete" aria-label={`Delete preset ${p.name}`}
                  onClick={(e) => deletePreset(e, p.id)}
                  className="text-slate-400 hover:text-rose-600 rounded-full focus:outline-none focus:ring-2 focus:ring-rose-400"><Trash2 className="w-3.5 h-3.5" /></button>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-3">
          <div><Label>Item</Label><Input data-testid="sourcing-item" value={f.item_name} onChange={(e) => set("item_name", e.target.value)} placeholder="e.g. Dermapen Cartridges" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Quantity</Label><Input type="number" value={f.qty} onChange={(e) => set("qty", e.target.value)} /></div>
            <div><Label>Import Origin</Label>
              <Select value={f.import_origin} onValueChange={(v) => set("import_origin", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["China", "Korea", "USA", "Germany", "Other"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div className="rounded-lg border border-slate-200 p-3 bg-slate-50/60">
            <div className="flex items-center gap-2 text-sm font-semibold text-navy mb-2"><Store className="w-4 h-4 text-brand-teal" /> Local Vendor</div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Price / unit (₹)</Label><Input data-testid="sourcing-local" type="number" value={f.local_price} onChange={(e) => set("local_price", e.target.value)} /></div>
              <div><Label>Lead time (days)</Label><Input type="number" value={f.lead_time_local} onChange={(e) => set("lead_time_local", e.target.value)} /></div>
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 p-3 bg-slate-50/60">
            <div className="flex items-center gap-2 text-sm font-semibold text-navy mb-2"><Ship className="w-4 h-4 text-brand-teal" /> Import</div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Price / unit (₹)</Label><Input data-testid="sourcing-import" type="number" value={f.import_price} onChange={(e) => set("import_price", e.target.value)} /></div>
              <div><Label>Customs duty %</Label><Input type="number" value={f.duty_pct} onChange={(e) => set("duty_pct", e.target.value)} /></div>
              <div><Label>Total shipping (₹)</Label><Input type="number" value={f.shipping_total} onChange={(e) => set("shipping_total", e.target.value)} /></div>
              <div><Label>Lead time (days)</Label><Input type="number" value={f.lead_time_import} onChange={(e) => set("lead_time_import", e.target.value)} /></div>
            </div>
          </div>
          <Button data-testid="sourcing-run" onClick={run} disabled={loading} className="w-full bg-brand-teal hover:bg-brand-tealdark gap-2">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <BrainCircuit className="w-4 h-4" />} Compare & Get AI Verdict
          </Button>
          <div className="flex gap-2 pt-1">
            <Input data-testid="preset-name" value={presetName} onChange={(e) => setPresetName(e.target.value)} placeholder="Name this comparison to reuse later…" className="h-9" />
            <Button data-testid="save-preset" variant="outline" onClick={savePreset} disabled={!f.item_name} className="h-9 gap-1.5 shrink-0"><Save className="w-4 h-4" /> Save</Button>
          </div>
        </div>

        <div>
          {!res ? <Empty icon={Ship} msg="Enter figures and run the comparison" /> : (
            <div className="space-y-4" data-testid="sourcing-result">
              <div className="grid grid-cols-2 gap-3">
                <div className={`rounded-lg p-4 border-2 ${!cheaperImport ? "border-brand-teal bg-teal-50" : "border-slate-200"}`}>
                  <div className="text-xs uppercase tracking-widest text-brand-slate">Local Total</div>
                  <div className="font-heading text-xl font-bold text-navy mt-1">{inr(c.local_total)}</div>
                  <div className="text-xs text-brand-slate mt-1">{f.lead_time_local}d lead</div>
                </div>
                <div className={`rounded-lg p-4 border-2 ${cheaperImport ? "border-brand-teal bg-teal-50" : "border-slate-200"}`}>
                  <div className="text-xs uppercase tracking-widest text-brand-slate">Import Landed Total</div>
                  <div className="font-heading text-xl font-bold text-navy mt-1">{inr(c.import_total)}</div>
                  <div className="text-xs text-brand-slate mt-1">{inr(c.import_landed_unit)}/unit · {f.lead_time_import}d lead</div>
                </div>
              </div>
              <div className={`rounded-lg p-4 ${c.savings > 0 ? "bg-emerald-50" : "bg-rose-50"}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-navy">{c.savings > 0 ? "Import saves" : "Import costs extra"}</span>
                  <span className={`font-heading text-2xl font-bold ${c.savings > 0 ? "text-brand-emerald" : "text-rose-600"}`}>{inr(Math.abs(c.savings))}</span>
                </div>
                <div className="text-xs text-brand-slate mt-1">{pct(Math.abs(c.savings_pct))} vs local · +{c.extra_lead_days} extra lead days</div>
              </div>
              <div className="bg-white border border-border rounded-lg p-4 space-y-0.5">{renderMd(res.verdict)}</div>
              {res.history?.length > 0 && <div className="text-xs text-brand-slate">Local price history: {res.history.slice(0, 3).map((h) => `${h.vendor} ${inr(h.unit_price)}`).join(" · ")}</div>}
            </div>
          )}
        </div>
      </div>
    </SectionCard>
  );
}

export default function Analytics() {
  const { branch } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [answer, setAnswer] = useState("");
  const [qLoading, setQLoading] = useState(false);
  const [insights, setInsights] = useState("");
  const [insLoading, setInsLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/analytics", { params: { branch } }).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [branch]);

  const ask = async () => {
    if (!q.trim()) return;
    setQLoading(true); setAnswer("");
    try { const r = await api.post("/ai/query", { query: q.trim() }); setAnswer(r.data.answer); }
    catch { setAnswer("AI service unavailable."); }
    finally { setQLoading(false); }
  };

  const genInsights = async () => {
    setInsLoading(true);
    try { const r = await api.get("/ai/insights"); setInsights(r.data.answer); }
    catch { setInsights("AI service unavailable."); }
    finally { setInsLoading(false); }
  };

  if (loading || !data) return <div className="text-brand-slate">Loading analytics…</div>;

  const SUGGEST = [
    "Which branch spent the most on Cosmeceuticals?",
    "Show delayed orders and their vendors",
    "Which vendor gives the best savings?",
  ];

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <h1 className="font-heading text-3xl font-bold text-navy tracking-tight flex items-center gap-2"><BrainCircuit className="w-7 h-7 text-brand-teal" /> AI Analytics & Intelligence</h1>
        <p className="text-brand-slate text-sm mt-1">Management analytics {branch === "All" ? "across all branches" : `for ${branch}`} — ask questions or use the ready-made insights below</p>
      </div>

      {/* NL query */}
      <div className="bg-gradient-to-r from-navy to-brand-tealdark rounded-xl p-5">
        <div className="flex items-center gap-2 text-white mb-3"><Sparkles className="w-4 h-4 text-teal-200" /> <span className="font-semibold">Ask Procurement Intelligence</span></div>
        <div className="flex gap-2">
          <input data-testid="analytics-ai-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()}
            placeholder="e.g. Summarize savings across Sarita Vihar this year"
            className="flex-1 h-11 px-4 rounded-lg bg-white/95 border-0 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-teal-300" />
          <Button data-testid="analytics-ai-send" onClick={ask} disabled={qLoading} className="h-11 bg-brand-teal hover:bg-brand-emerald gap-2 px-5">
            {qLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Ask
          </Button>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          {SUGGEST.map((s) => <button key={s} onClick={() => { setQ(s); }} className="text-xs text-teal-100 bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-full">{s}</button>)}
        </div>
        {answer && <div className="mt-4 bg-white rounded-lg p-4 space-y-0.5" data-testid="analytics-ai-answer">{renderMd(answer)}</div>}
      </div>

      <AiTotals t={data.totals} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard title="Monthly Spend vs Savings">
          {data.monthly.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={data.monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748B" }} />
                <YAxis tickFormatter={inrShort} tick={{ fontSize: 12, fill: "#64748B" }} width={55} />
                <Tooltip formatter={(v) => inr(v)} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="spend" name="Spend" stroke="#0F172A" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="saved" name="Saved" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : <Empty msg="No data" />}
        </SectionCard>

        <SectionCard title="Payment Status Breakdown">
          {data.payment_breakdown.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={data.payment_breakdown} dataKey="value" nameKey="name" innerRadius={55} outerRadius={100} paddingAngle={2}>
                  {data.payment_breakdown.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => inr(v)} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <Empty msg="No data" />}
        </SectionCard>

        <SectionCard title="Savings by Branch">
          {data.savings_by_branch.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.savings_by_branch}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748B" }} />
                <YAxis tickFormatter={inrShort} tick={{ fontSize: 12, fill: "#64748B" }} width={55} />
                <Tooltip formatter={(v) => inr(v)} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="spend" name="Spend" fill="#64748B" radius={[4, 4, 0, 0]} />
                <Bar dataKey="saved" name="Saved" fill="#059669" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <Empty msg="No data" />}
        </SectionCard>

        <SectionCard title="Spend by Category">
          {data.savings_by_category.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.savings_by_category} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" horizontal={false} />
                <XAxis type="number" tickFormatter={inrShort} tick={{ fontSize: 12, fill: "#64748B" }} />
                <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 10, fill: "#64748B" }} />
                <Tooltip formatter={(v) => inr(v)} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }} />
                <Bar dataKey="spend" name="Spend" fill="#0D9488" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <Empty msg="No data" />}
        </SectionCard>
      </div>

      <SourcingTool />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard title="Vendor Scorecard">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-border"><tr>{["Vendor", "Rating", "Orders", "Spend", "Saved", "On-Time"].map((h) => <Th key={h}>{h}</Th>)}</tr></thead>
              <tbody>
                {data.vendor_scorecard.map((v) => (
                  <tr key={v.vendor} data-testid="scorecard-row" className="border-b border-border/60 hover:bg-slate-50">
                    <Td className="font-medium">{v.vendor}</Td>
                    <Td><Stars rating={v.rating} /></Td>
                    <Td>{v.orders}</Td>
                    <Td className="tabular-nums font-semibold">{inr(v.spend)}</Td>
                    <Td className="tabular-nums text-brand-emerald">{inr(v.saved)}</Td>
                    <Td className={v.on_time_rate != null && v.on_time_rate < 0.8 ? "text-rose-600" : "text-brand-emerald"}>{v.on_time_rate == null ? "—" : pct(v.on_time_rate)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>

        <SectionCard title="AI Executive Insights" action={<Button data-testid="gen-insights" size="sm" onClick={genInsights} disabled={insLoading} className="bg-brand-teal hover:bg-brand-tealdark gap-1.5">{insLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Generate</Button>}>
          {insights ? <div className="space-y-0.5" data-testid="insights-content">{renderMd(insights)}</div>
            : <Empty icon={BrainCircuit} msg="Click Generate for a monthly executive AI summary" />}
        </SectionCard>
      </div>

      <SectionCard title="Top Savings Wins">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-border"><tr>{["Item", "Vendor", "Branch", "Saved", "Savings %"].map((h) => <Th key={h}>{h}</Th>)}</tr></thead>
            <tbody>
              {data.top_saved.map((t, i) => (
                <tr key={i} className="border-b border-border/60 hover:bg-slate-50">
                  <Td className="font-medium max-w-[240px] truncate">{t.item_name}</Td>
                  <Td className="text-slate-600">{t.vendor}</Td>
                  <Td className="text-slate-600">{t.branch}</Td>
                  <Td className="tabular-nums text-brand-emerald font-semibold">{inr(t.saved)}</Td>
                  <Td className="text-brand-emerald">{pct(t.savings_pct)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}
